import dayjs from 'dayjs'

export type HubSpotEngagementType = 'call' | 'note' | 'logged-email' | 'task' | 'meeting'

export type EngagementConfig = {
	title: string
	url: string
	placeholder: string
	successMessage: string
	/** Label of the datetime field. If unset, no datetime field is shown. */
	dateTimeLabel?: string
}

export type SelectOption<T = string> = {
	value: T
	label: string
}

export type EngagementPayload = {
	infoText: string
	contactId: string | null
	timestamp: string | null
	callDisposition: string | null
	callDirection: string | null
	taskReminder: string | null
	taskType: string | null
	taskPriority: string | null
	meetingOutcome: string | null
	meetingDurationMinutes: number | null
}

export const ENGAGEMENT_CONFIG: Record<HubSpotEngagementType, EngagementConfig> = {
	call: {
		title: 'Anruf in HubSpot loggen',
		url: '/api/hubspot/protocolCall',
		placeholder: 'Was wurde besprochen?',
		successMessage: 'Der Anruf wurde in HubSpot gespeichert.',
		dateTimeLabel: 'Zeitpunkt des Anrufs'
	},
	note: {
		title: 'Notiz in HubSpot erstellen',
		url: '/api/hubspot/createNote',
		placeholder: 'Inhalt der Notiz',
		successMessage: 'Die Notiz wurde in HubSpot erstellt.'
	},
	'logged-email': {
		title: 'E-Mail in HubSpot loggen',
		url: '/api/hubspot/logMail',
		placeholder: 'Inhalt der E-Mail',
		successMessage: 'Die E-Mail wurde in HubSpot geloggt.'
	},
	task: {
		title: 'Aufgabe in HubSpot erstellen',
		url: '/api/hubspot/createTask',
		placeholder: 'Beschreibung der Aufgabe',
		successMessage: 'Die Aufgabe wurde in HubSpot erstellt.',
		dateTimeLabel: 'Fälligkeitsdatum'
	},
	meeting: {
		title: 'Meeting in HubSpot loggen',
		url: '/api/hubspot/logMeeting',
		placeholder: 'Was wurde besprochen?',
		successMessage: 'Das Meeting wurde in HubSpot geloggt.',
		dateTimeLabel: 'Startzeit'
	}
}

/** HubSpot-internal GUIDs of the default call outcomes (hs_call_disposition) */
export const CALL_DISPOSITION_OPTIONS: SelectOption[] = [
	{ value: '', label: '–' },
	{ value: 'f240bbac-87c9-4f6e-bf70-924b57d47db7', label: 'Verbunden' },
	{ value: '9d9162e7-6cf3-4944-bf63-4dff82258764', label: 'Besetzt' },
	{ value: 'a4c4c377-d246-4b32-a13b-75a56a4cd0ff', label: 'Live-Nachricht hinterlassen' },
	{ value: 'b2cf5968-551e-4856-9783-52b3da59a7d0', label: 'Voicemail hinterlassen' },
	{ value: '73a0d17f-1163-4015-bdd5-ec830791da20', label: 'Keine Antwort' },
	{ value: '17b47fee-58de-441e-a44c-c6300d46f273', label: 'Falsche Nummer' }
]

/** hs_call_direction */
export const CALL_DIRECTION_OPTIONS: SelectOption[] = [
	{ value: '', label: '–' },
	{ value: 'OUTBOUND', label: 'Ausgehend' },
	{ value: 'INBOUND', label: 'Eingehend' }
]

/** Reminder as offset relative to the due date (hs_task_reminders takes a timestamp) */
export const TASK_REMINDER_OPTIONS: (SelectOption & { offsetMinutes: number | null })[] = [
	{ value: '', label: 'Keine Erinnerung', offsetMinutes: null },
	{ value: 'AT_DUE', label: 'Zum Fälligkeitszeitpunkt', offsetMinutes: 0 },
	{ value: 'MIN_30', label: '30 Minuten vorher', offsetMinutes: 30 },
	{ value: 'HOUR_1', label: '1 Stunde vorher', offsetMinutes: 60 },
	{ value: 'DAY_1', label: '1 Tag vorher', offsetMinutes: 60 * 24 },
	{ value: 'WEEK_1', label: '1 Woche vorher', offsetMinutes: 60 * 24 * 7 }
]

/** hs_task_type */
export const TASK_TYPE_OPTIONS: SelectOption[] = [
	{ value: 'TODO', label: 'To-do' },
	{ value: 'CALL', label: 'Anruf' },
	{ value: 'EMAIL', label: 'E-Mail' }
]

/** hs_task_priority ('' = property is not set at all) */
export const TASK_PRIORITY_OPTIONS: SelectOption[] = [
	{ value: '', label: 'Keine' },
	{ value: 'LOW', label: 'Niedrig' },
	{ value: 'MEDIUM', label: 'Mittel' },
	{ value: 'HIGH', label: 'Hoch' }
]

/** hs_meeting_outcome */
export const MEETING_OUTCOME_OPTIONS: SelectOption[] = [
	{ value: '', label: '–' },
	{ value: 'SCHEDULED', label: 'Geplant' },
	{ value: 'COMPLETED', label: 'Stattgefunden' },
	{ value: 'RESCHEDULED', label: 'Verschoben' },
	{ value: 'NO_SHOW', label: 'Nicht erschienen' },
	{ value: 'CANCELED', label: 'Abgesagt' }
]

function formatDurationLabel(minutes: number): string {
	const hours = Math.floor(minutes / 60)
	const remainingMinutes = minutes % 60
	const parts: string[] = []
	if (hours > 0) {
		parts.push(hours === 1 ? '1 Stunde' : `${hours} Stunden`)
	}
	if (remainingMinutes > 0) {
		parts.push(`${remainingMinutes} Minuten`)
	}
	return parts.join(' ')
}

/** Meeting duration in 15 minute steps up to 8 hours, used to compute hs_meeting_end_time */
export const MEETING_DURATION_OPTIONS: SelectOption<number>[] = Array.from(
	{ length: (8 * 60) / 15 },
	(_, i) => {
		const minutes = (i + 1) * 15
		return { value: minutes, label: formatDurationLabel(minutes) }
	}
)

/**
 * Computes the reminder timestamp (ISO string) from the selected reminder option relative to the
 * due date (unix timestamp in seconds). Returns null for "Keine Erinnerung".
 */
export function buildReminderTimestamp(
	reminderValue: string,
	dueDateUnix: number | null
): string | null {
	const option = TASK_REMINDER_OPTIONS.find((o) => o.value === reminderValue)
	// == null (not !dueDateUnix): unix 0 is a valid due date; truthiness would drop it
	if (!option || option.offsetMinutes === null || dueDateUnix == null) {
		return null
	}
	return dayjs.unix(dueDateUnix).subtract(option.offsetMinutes, 'minute').toISOString()
}
